// Roles × permissions matrix.  Cell toggles emit onToggle(roleId, permId, nextChecked).
// Classification column shows a chip per role.

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function RbacMatrix({
  roles = [],
  permissions = [],
  rolePermissions = new Map(), // Map<roleId, Set<permId>>
  grants = [],
  onToggle,
  classificationFor,
  readOnly = false,
}) {
  const tooltip = useOptionalRichTooltipController();
  if (!roles.length || !permissions.length) return null;
  const grantMap = new Map(grants.map((grant) => [`${grant.roleId}:${grant.permissionId}`, grant]));
  return (
    <div className="rbac-matrix">
      <div
        className="rbac-grid"
        style={{
          gridTemplateColumns: `220px repeat(${permissions.length}, 120px) 140px`,
        }}
      >
        <div className="rbac-corner">Role</div>
        {permissions.map((p) => (
          <div key={p.id} className="rbac-col-header">
            {p.name || p.id.replace('PERM-', '')}
          </div>
        ))}
        <div className="rbac-col-header right">Classification</div>
        {roles.map((r) => {
          const granted = rolePermissions.get(r.id) || new Set();
          const cls = (r.classificationTone || classificationFor?.(r) || 'slate').toLowerCase();
          return (
            <RoleRow
              key={r.id}
              role={r}
              permissions={permissions}
              granted={granted}
              onToggle={onToggle}
              classificationTone={cls}
              grantMap={grantMap}
              tooltip={tooltip}
              readOnly={readOnly}
            />
          );
        })}
      </div>
    </div>
  );
}

function RoleRow({
  role,
  permissions,
  granted,
  onToggle,
  classificationTone,
  grantMap,
  tooltip,
  readOnly,
}) {
  return (
    <>
      <div className="rbac-role">
        <strong>{role.name}</strong>
        <div className="muted small">{role.description?.slice(0, 60) || role.id}</div>
      </div>
      {permissions.map((p) => {
        const cell = grantMap.get(`${role.id}:${p.id}`);
        const isGranted = cell ? cell.granted : granted.has(p.id);
        const active = Boolean(
          cell?.richTooltip?.surfaceId && tooltip?.isActive(cell.richTooltip.surfaceId),
        );
        const pinned = Boolean(
          cell?.richTooltip?.surfaceId && tooltip?.isPinned(cell.richTooltip.surfaceId),
        );
        const triggerProps =
          cell?.richTooltip && tooltip
            ? tooltip.getTriggerProps({
                surfaceId: cell.richTooltip.surfaceId,
                payload: cell.richTooltip,
                testIdPrefix: 'rich-tooltip',
                ariaLabel: `${role.name} ${isGranted ? 'granted' : 'not granted'} ${p.name}`,
              })
            : {};
        return (
          <button
            key={p.id}
            type="button"
            className={`rbac-cell ${isGranted ? 'granted' : ''}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}
            onClick={
              !cell?.richTooltip && !readOnly && onToggle
                ? () => onToggle(role.id, p.id, !isGranted)
                : undefined
            }
            aria-pressed={isGranted}
            aria-label={`${role.name} ${isGranted ? 'has' : 'does not have'} ${p.name}`}
            data-testid={`rbac-cell-${role.id}-${p.id}`}
            {...triggerProps}
          >
            {isGranted ? '●' : '○'}
          </button>
        );
      })}
      <div className="rbac-classification">
        <span className={`chip ${classificationTone} sm`}>
          {role.classificationLabel || 'Unclassified'}
        </span>
      </div>
    </>
  );
}
